// Microsoft Clarity loader - session replay + heatmaps. Idempotent: calling
// load() twice is safe.
//
// CONSENT-GATED, and deliberately not in Umami's pre-consent tier. Clarity
// writes first-party cookies (_clck / _clsk) AND records a replay of what the
// visitor does on the page, so both triggers fire: ePrivacy device storage and
// GDPR personal-data processing. It belongs with GA4/GTM, behind consent.
//
// CSP: CloudFront must allow clarity.ms in script-src and *.clarity.ms +
// c.bing.com in connect-src (helinhair-infrastructure PR #63). Clarity splits
// tag delivery from data ingest, so with script-src alone the tag loads and
// then silently records nothing. It only fails behind CloudFront - localhost
// has no CSP - so this cannot be verified in dev.
//
// BUG-527: once loaded (so only after Accept) Clarity also gets the consent
// signal (`consentv2`, analytics granted, ads denied), session tags (lang,
// internal, salon_id, booking_vendor, shop_step) and a short list of custom
// events mirrored from track(). Markup with personal data carries
// `data-clarity-mask="true"` on top of the project's masking setting.

import type { ConsentStatus } from '../consent'
import { isInternalTraffic } from './internal'
import { isSensitiveRoute } from './redact'

const DEFAULT_CLARITY_ID = 'y2a6nbhlq8'
const SCRIPT_ATTR = 'data-helin-clarity'

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] }

interface ClarityWindow extends Window {
  clarity?: ClarityFn
}

export function getClarityId(): string {
  const fromEnv = (import.meta.env.VITE_CLARITY_ID as string | undefined)?.trim()
  return fromEnv && fromEnv.length > 0 ? fromEnv : DEFAULT_CLARITY_ID
}

export function isClarityLoaded(): boolean {
  return document.querySelector(`script[${SCRIPT_ATTR}]`) !== null
}

export function loadClarity(id: string = getClarityId()): void {
  if (isClarityLoaded()) return
  if (!id) return
  // Never start session replay on an order page: the replay would show the
  // buyer's name and address. syncClarityRoute loads it on the next page.
  if (isSensitiveRoute(window.location.pathname)) return

  // The vendor snippet installs a queueing stub so `clarity(...)` calls made
  // before the tag finishes downloading are replayed once it boots. Same
  // contract as GTM's dataLayer - keep it, or early calls are dropped.
  const w = window as ClarityWindow
  if (!w.clarity) {
    const stub: ClarityFn = (...args: unknown[]) => {
      stub.q = stub.q ?? []
      stub.q.push(args)
    }
    w.clarity = stub
  }

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.clarity.ms/tag/${encodeURIComponent(id)}`
  script.setAttribute(SCRIPT_ATTR, id)
  document.head.appendChild(script)

  // Clarity only ever loads after Accept, so it starts with consent granted.
  // Queued on the stub, these reach the tag as soon as it boots.
  w.clarity!('consentv2', clarityConsent('granted'))
  const lang = /^\/([a-z]{2})(?:\/|$)/.exec(window.location.pathname)?.[1]
  if (lang) w.clarity!('set', 'lang', lang)
  if (isInternalTraffic()) w.clarity!('set', 'internal', '1')
}

function clarityConsent(status: ConsentStatus): Record<string, string> {
  return { ad_Storage: 'denied', analytics_Storage: status === 'granted' ? 'granted' : 'denied' }
}

/**
 * The banner's decision changed after Clarity loaded (consent withdrawn, or
 * given again): pass it on. No-op while Clarity is not loaded.
 */
export function applyClarityConsent(status: ConsentStatus): void {
  const w = window as ClarityWindow
  if (!w.clarity || !isClarityLoaded()) return
  w.clarity('consentv2', clarityConsent(status))
}

/** track() events Clarity also gets as custom events (plan C2). */
export const CLARITY_EVENTS: ReadonlySet<string> = new Set([
  'book_now_click',
  'booking_handoff',
  'call_salon_click',
  'concierge_open',
  'newsletter_signup',
  'contact_form_submit',
  'add_to_cart',
  'begin_checkout',
  'add_payment_info',
  'purchase',
  'discount_apply',
])

/** The shop funnel step a recording reached, as a session tag (plan C3). */
const SHOP_STEPS: Readonly<Record<string, string>> = {
  view_cart: 'cart',
  begin_checkout: 'checkout',
  add_payment_info: 'payment',
}

/** Session tags read from an event's (already redacted) params. */
const TAG_PARAMS = ['salon_id', 'booking_vendor'] as const

/**
 * Called by track() after the consent gate, with redacted params: sets the
 * session tags the event carries and sends the custom event when it is on
 * the list. No-op while Clarity is not loaded (no consent, or non-prod).
 */
export function clarityTrack(event: string, params: Readonly<Record<string, unknown>>): void {
  const w = window as ClarityWindow
  if (!w.clarity || !isClarityLoaded()) return
  for (const key of TAG_PARAMS) {
    const value = params[key]
    if (typeof value === 'string' && value !== 'unset') w.clarity('set', key, value)
  }
  if (Object.hasOwn(SHOP_STEPS, event)) w.clarity('set', 'shop_step', SHOP_STEPS[event])
  if (CLARITY_EVENTS.has(event)) w.clarity('event', event)
}

let stopped = false

/**
 * SPA route change: stop Clarity's recording on the order pages and restart it
 * when the visitor leaves them. No-op while Clarity is not loaded.
 */
export function syncClarityRoute(pathname: string): void {
  const w = window as ClarityWindow
  if (!w.clarity || !isClarityLoaded()) return
  const sensitive = isSensitiveRoute(pathname)
  if (sensitive && !stopped) {
    w.clarity('stop')
    stopped = true
  } else if (!sensitive && stopped) {
    w.clarity('start')
    stopped = false
  }
}

export function _resetClarityForTesting(): void {
  stopped = false
}
